import { useRequiredContext } from "@redotech/react-util/context";
import {
  supportCustomActionContractV1,
  supportCustomActionStarterCode,
} from "@redotech/redo-extensions-common";
import type { RunCustomCodeAction as RunCustomCodeActionType } from "@redotech/redo-flows-common/rules/schemas";
import { T, useTranslation } from "@redotech/redo-i18n";
import PenIcon from "@redotech/redo-icons/edit-03_filled.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoTagsDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { CodeEditorInputField } from "@redotech/redo-web/code-editor";
import { ExtensionArtifactCodeEditorModal } from "@redotech/redo-web/extension-artifact-code-editor-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";

export const RunCustomCodeAction = memo(function RunCustomCodeAction({
  action,
  onChange,
  editableFields,
}: {
  action: RunCustomCodeActionType;
  onChange: (updated: RunCustomCodeActionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const editable =
    editableFields === undefined || editableFields.has("codeArtifactId");
  const merchantRpc = useRequiredContext(RedoMerchantRpcClientContext);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectorDropdownOpen, setSelectorDropdownOpen] = useState(false);
  const [selectorAnchor, setSelectorAnchor] =
    useState<HTMLButtonElement | null>(null);

  return (
    <Flex dir="column" gap="sm" w="full">
      {action.codeArtifactId ? (
        <Text fontFamily="monospace" fontSize="xs" textColor="secondary">
          {t("Artifact {{id}}", { id: action.codeArtifactId })}
        </Text>
      ) : (
        <Text fontSize="xs" textColor="secondary">
          {t("No code uploaded yet")}
        </Text>
      )}

      <Flex>
        <RedoButton
          disabled={!editable}
          hierarchy="primary"
          IconLeading={PenIcon}
          onClick={() => setEditorOpen(true)}
          size="xs"
          text={action.codeArtifactId ? t("Edit code") : t("Add code")}
        />
      </Flex>

      <ExtensionArtifactCodeEditorModal
        artifactId={action.codeArtifactId || undefined}
        client={merchantRpc}
        contractId={supportCustomActionContractV1.id}
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={(codeArtifactId) => {
          onChange({ ...action, codeArtifactId });
          setEditorOpen(false);
        }}
        readonly={!editable}
        renderRightPane={({
          dts,
          universeDts,
          topLevelKinds,
          inputSelector,
          setInputSelector,
        }) => {
          const kindItems: RedoListItem<string>[] = topLevelKinds.map(
            (kind) => ({ id: kind, type: "text", text: kind, value: kind }),
          );
          const selectedKindItems = kindItems.filter((item) =>
            inputSelector.includes(item.value),
          );
          return (
            <>
              <Flex dir="column" gap="xs">
                <Text fontSize="xs" fontWeight="medium">
                  {t("Input selector")}
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  <T>
                    Pick the top-level fields your code reads. Unselected fields
                    are pruned from <code>Input</code>.
                  </T>
                </Text>
                <RedoTagsDropdownInputButton<string>
                  disabled={!editable}
                  isDropdownOpen={selectorDropdownOpen}
                  onClick={() =>
                    editable && setSelectorDropdownOpen((open) => !open)
                  }
                  placeholder={t("Select fields...")}
                  ref={setSelectorAnchor}
                  removeItem={(item) => {
                    if (!editable) {
                      return;
                    }
                    setInputSelector((prev) =>
                      prev.filter((k) => k !== item.value),
                    );
                  }}
                  selectedItems={selectedKindItems.map((item) => ({
                    id: item.id,
                    type: "badge",
                    badge: { text: item.value },
                    value: item.value,
                  }))}
                  size="xs"
                />
                <RedoMultiselectDropdown<string>
                  dropdownAnchor={selectorAnchor}
                  dropdownOpen={selectorDropdownOpen}
                  fitToAnchor
                  items={kindItems}
                  selectedItems={selectedKindItems}
                  selectionVariant="checkbox"
                  setDropdownOpen={(open) => {
                    if (editable) {
                      setSelectorDropdownOpen(open);
                    }
                  }}
                  setSelectedItems={(items) => {
                    if (editable) {
                      setInputSelector(items.map((item) => item.value));
                    }
                  }}
                  size="xs"
                />
              </Flex>

              <Flex dir="column" gap="xs">
                <Text fontSize="xs" fontWeight="medium">
                  {t("Available input")}
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  {t(
                    "The Input type your code receives, narrowed by your input selector.",
                  )}
                </Text>
                <CodeEditorInputField
                  language="javascript"
                  readonly
                  setValue={() => {}}
                  value={dts}
                />
                <RedoCollapseSubsection title={t("Show full schema")}>
                  <Flex dir="column" gap="xs" pt="xs">
                    <CodeEditorInputField
                      language="javascript"
                      readonly
                      setValue={() => {}}
                      value={universeDts}
                    />
                  </Flex>
                </RedoCollapseSubsection>
              </Flex>
            </>
          );
        }}
        starterCode={supportCustomActionStarterCode}
        subtitle={t(
          "Run merchant-authored TypeScript against the support ticket.",
        )}
        title={t("Edit custom code action")}
      />
    </Flex>
  );
});
